<script lang="ts">
  import { ApiTypes } from '../enums/api.enum';
  import { HandledErrorTypes } from '../enums/handled-errors.enum';
  import {
    state_apiErrors,
    state_busyApi,
    state_conversationResults,
  } from '../store/state';
  import { ConfigStore } from './../store/config';

  import { fade } from 'svelte/transition';
  import BusyComponent from './busy.svelte';
  import ChatResultItemComponent from './chat-result-item.svelte';
  import FaqComponent from './faq.svelte';
  import { _ } from 'svelte-i18n';
  import { onDestroy, tick } from 'svelte';
  import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';

  $: isSearchBusy = $state_busyApi.some((x) =>
    [
      ApiTypes.postQuestion,
      ApiTypes.streamPOSTQuestion, // added 07/24
      ApiTypes.postOption,
      ApiTypes.getOption,
      ApiTypes.getThread,
    ].includes(x)
  );

  $: postQuestionApiError = $state_apiErrors.find((x) =>
    [ApiTypes.postQuestion, ApiTypes.streamPOSTQuestion].includes(x.apiType)
  );

  let wasSearchBusy = isSearchBusy;

  let scrollContainer: HTMLDivElement;
  let liveRegionText = '';
  let announceTimer: ReturnType<typeof setTimeout>;

  $: {
    if (wasSearchBusy === false && isSearchBusy === true) {
      announceToScreenReader('Loading, please wait.', {
        focusSearchInputFirst: true,
      });
    }

    if (wasSearchBusy === true && isSearchBusy === false) {
      const answer =
        $state_conversationResults[$state_conversationResults.length - 1]
          ?.answer;

      announceToScreenReader(getPlainText(answer), {
        focusSearchInputFirst: true,
        prefix: $ConfigStore.agentName,
      });
    }
    // Update the tracker for the next change
    wasSearchBusy = isSearchBusy;
  }

  const handleAnnounceEvent = (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    if (detail.clear) {
      // Blank the live region (e.g. suggestions hidden) so a stale message
      // isn't left for the screen reader.
      clearTimeout(announceTimer);
      liveRegionText = '';
      return;
    }
    announceToScreenReader(detail.message ?? '', {
      focusSearchInputFirst: detail.focusSearchInputFirst,
      prefix: detail.prefix,
    });
  };

  window.addEventListener(
    WindowsCustomEventTypes.announceToScreenReader,
    handleAnnounceEvent
  );

  const announceToScreenReader = async (
    message: string = '',
    options: {
      focusSearchInputFirst?: boolean;
      prefix?: string;
    } = {}
  ) => {
    const cleanMessage = message.trim();
    if (!cleanMessage) return;

    clearTimeout(announceTimer);
    if (options.focusSearchInputFirst) {
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.focusSearchInput)
      );
    }

    liveRegionText = '';
    await tick();

    announceTimer = setTimeout(async () => {
      liveRegionText = options.prefix
        ? `${options.prefix}: ${cleanMessage}`
        : cleanMessage;
    }, 75);
  };

  const getPlainText = (html: string = '') => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const textContent = doc.body.textContent || '';

    // Regular expression to match most common emoji
    const emojiRegex =
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;

    // Replace emoji with an empty string
    return textContent.replace(emojiRegex, '');
  };

  onDestroy(() => {
    clearTimeout(announceTimer);
    window.removeEventListener(
      WindowsCustomEventTypes.announceToScreenReader,
      handleAnnounceEvent
    );
  });
</script>

{#if $ConfigStore.customCSSUrl}
  {#if $ConfigStore.customCSSIntegrity}
    <link
      href={$ConfigStore.customCSSUrl}
      rel="stylesheet"
      type="text/css"
      integrity={$ConfigStore.customCSSIntegrity}
      crossorigin="anonymous"
    />
  {:else}
    <link href={$ConfigStore.customCSSUrl} rel="stylesheet" type="text/css" />
  {/if}
{/if}
{#if !!$ConfigStore.apiBaseUrl}
  <div class="sr-only" aria-live="polite" aria-atomic="true">
    {liveRegionText}
  </div>
  <div
    class={`chat-item-wrapper  ${$ConfigStore.widgetType ? 'cs-type-' + $ConfigStore?.widgetType : ''}
    ${$ConfigStore?.isIdentifyName ? 'cs-identify-name' : ''}`}
    bind:this={scrollContainer}
    role="region"
    aria-label="Chat messages"
  >
    {#if $state_conversationResults.length}
      {#each $state_conversationResults as _item, i}
        <ChatResultItemComponent
          index={i}
          indexOfLastItem={i == $state_conversationResults.length - 1}
        />
      {/each}

      {#if $state_conversationResults.length == 1 && $state_conversationResults[0].is_greeting}
        <div class="cs-search-faq" in:fade|global>
          <FaqComponent />
        </div>
      {/if}
    {:else}
      <div class="cs-search-faq" in:fade|global>
        <FaqComponent />
      </div>
    {/if}

    {#if postQuestionApiError}
      {#if postQuestionApiError.noticeCode === 'history_reset'}
        <div class="cs-error-msg" role="status">
          {$ConfigStore.historyResetMessage}
        </div>
      {:else if postQuestionApiError.error == HandledErrorTypes.GPTContentFilterError}
        <div class="cs-error-msg" role="alert">
          {$_('chat_wrapper.error_content_filter')}
        </div>
      {:else if postQuestionApiError.status == 429}
        <div class="cs-error-msg" role="alert">
          {$_('chat_wrapper.error_too_many_requests')}
        </div>
      {:else if postQuestionApiError.errorType != 'AbortError'}
        <div class="cs-error-msg" role="alert">
          {$_('chat_wrapper.error_general')}
        </div>
      {/if}
    {:else if $state_conversationResults.length && !isSearchBusy}
      <!-- {#if $ConfigStore.suggestQuestions !== 'never' && $state_questionRelatedSearchSuggestions}
        <div in:fly|global>
          <QuestionRelatedSuggestionComponents />
        </div>
      {/if} -->
    {/if}
    {#if !$ConfigStore.scrollBehavior}
      <div
        class="cs-search-loading-chat"
        class:is-hidden={!isSearchBusy}
        class:cs-scrolling-behavior-loading={$ConfigStore.scrollBehavior}
        in:fade|global
      >
        <BusyComponent />
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  @use './../../scss/components/chat-wrapper';
  @use './../../scss/shared/_error';

  .cs-error-msg {
    margin: 10px 15px 0;
  }
</style>
